import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowUpRight,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleX,
  Clock,
  File as FileIcon,
  FileImage,
  FileOutput,
  FileText,
  Inbox as InboxIcon,
  Layers,
  MessageSquareText,
  ReceiptText,
  Search,
  Sparkles,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { DecisionDialog } from '@/components/portal/decision-dialog';
import { FileDrop } from '@/components/portal/file-drop';
import { FileLink } from '@/components/portal/file-link';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { formatAmount, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  documentPath,
  errorText,
  isClientRole,
  uploadFile,
  useInbox,
  useOrgRole,
  type InboxFilter,
  type InboxItem,
} from '@/lib/portal';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { homeCurrency, orgLocale } from '@/lib/org-locale';
import { useProcessInboxItem } from '@/lib/ai-accountant';

const CHIPS: Array<{ key: InboxFilter; label: string; icon: typeof InboxIcon }> = [
  { key: 'open', label: 'To review', icon: InboxIcon },
  { key: 'recorded', label: 'Recorded', icon: CircleCheck },
  { key: 'rejected', label: 'Not recorded', icon: CircleX },
  { key: 'all', label: 'All', icon: Layers },
];

const CHIP_TONE: Record<InboxFilter, string> = {
  open: 'text-info',
  recorded: 'text-success',
  rejected: 'text-destructive',
  all: 'text-primary',
};

const KINDS: Array<{
  value: InboxItem['kind'];
  label: string;
  hint: string;
  icon: typeof InboxIcon;
}> = [
  { value: 'bill', label: 'Bill', hint: 'To pay', icon: FileText },
  { value: 'receipt', label: 'Receipt', hint: 'Already paid', icon: ReceiptText },
  { value: 'invoice', label: 'Invoice', hint: 'Issued to a customer', icon: FileOutput },
  { value: 'other', label: 'Other', hint: 'Anything else', icon: FileIcon },
];

/** Chip counters from the full list (the API returns up to 500 uploads, newest first). */
function countByChip(items: InboxItem[] | undefined): Record<InboxFilter, number> | undefined {
  if (!items) return undefined;
  const c = { open: 0, recorded: 0, rejected: 0, all: items.length };
  for (const i of items) {
    if (i.status === 'recorded') c.recorded++;
    else if (i.status === 'rejected') c.rejected++;
    else c.open++;
  }
  return c;
}

function matches(i: InboxItem, needle: string): boolean {
  return [i.file.fileName, i.note, i.uploadedBy, i.document?.contactName, i.document?.number]
    .filter(Boolean)
    .some((v) => v!.toLowerCase().includes(needle));
}

/** Groups uploads under Today, Yesterday, Last 7 days and Older, in the client's time zone. */
type DayGroup = 'today' | 'yesterday' | 'lastWeek' | 'older';

function groupByDay(items: InboxItem[]): Array<[DayGroup, InboxItem[]]> {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: orgLocale().timeZone });
  const toDay = (d: Date) => Date.parse(day.format(d));
  const today = toDay(new Date());
  const groups = new Map<DayGroup, InboxItem[]>();
  for (const i of items) {
    const ago = Math.round((today - toDay(new Date(i.uploadedAt))) / 86_400_000);
    const label: DayGroup =
      ago <= 0 ? 'today' : ago === 1 ? 'yesterday' : ago < 7 ? 'lastWeek' : 'older';
    groups.set(label, [...(groups.get(label) ?? []), i]);
  }
  return [...groups];
}

export const Route = createFileRoute('/documents/inbox')({
  component: Inbox,
  validateSearch: (s: Record<string, unknown>): { status?: InboxFilter } =>
    typeof s['status'] === 'string' && CHIPS.some((c) => c.key === s['status'])
      ? { status: s['status'] as InboxFilter }
      : {},
});

/**
 * Document inbox (document 06, "Client portal"): the client uploads bills, receipts and invoices; Outss
 * staff turn each into a draft bill or expense, or say why it cannot be recorded.
 */
function Inbox() {
  const orgId = useOrgId();
  const role = useOrgRole();
  const staff = role !== null && !isClientRole(role);
  const canUpload = staff || role === 'client_owner';
  const { status = staff ? 'open' : 'all' } = Route.useSearch();
  const navigate = useNavigate();
  const list = useInbox(status);
  const all = useInbox('all');
  const [query, setQuery] = useState('');
  const qc = useQueryClient();
  const [kind, setKind] = useState<InboxItem['kind']>('bill');
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState<InboxItem | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const processAi = useProcessInboxItem();
  const t = useT();
  const sendToAi = (i: InboxItem) =>
    processAi.mutate(i.id, {
      onSuccess: (d) => {
        toast(t('inbox.sentToAi', { name: i.file.fileName }), {
          link: { to: `/ai-accountant/${d.id}`, label: t('inbox.open') },
        });
      },
      onError: (e) => toast(errorText(e), { tone: 'error' }),
    });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const q = new URLSearchParams({ kind, ...(note.trim() ? { note: note.trim() } : {}) });
      for (const f of files) await uploadFile<InboxItem>(`/orgs/${orgId}/inbox?${q}`, f);
    },
    onSuccess: (_r, files) => {
      toast(
        files.length === 1
          ? t('inbox.uploadedOne', { name: files[0]!.name })
          : t('inbox.uploadedMany', { count: files.length }),
      );
      setNote('');
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['portal', orgId] }),
  });
  const setStatus = useMutation({
    mutationFn: (v: {
      item: InboxItem;
      status: 'received' | 'in_review' | 'rejected';
      reason?: string;
    }) =>
      api<InboxItem>(`/orgs/${orgId}/inbox/${v.item.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: v.status, reason: v.reason, version: v.item.version }),
      }),
    onSuccess: (r) => {
      setRejecting(null);
      setRejectError(null);
      toast(
        t('inbox.statusChanged', {
          name: r.file.fileName,
          status: t(`inboxStatus.${r.status}`).toLowerCase(),
        }),
      );
    },
    onError: (e) => {
      if (rejecting) setRejectError(errorText(e));
      else toast(errorText(e), { tone: 'error' });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['portal', orgId] }),
  });

  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  const counts = countByChip(all.data);
  const needle = query.trim().toLowerCase();
  const rows = (list.data ?? []).filter((i) => !needle || matches(i, needle));
  const groups = groupByDay(rows);
  return (
    <>
      <PageHeader
        title={t('inbox.title')}
        description={staff ? t('inbox.staffDescription') : t('inbox.clientDescription')}
      />
      {canUpload ? (
        <section
          aria-label={t('inbox.upload')}
          className="mb-6 grid gap-4 rounded-xl border bg-card p-4 shadow-xs md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:p-5"
        >
          <div className="space-y-4">
            <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-medium">{t('inbox.whatIsIt')}</legend>
              <div
                role="radiogroup"
                aria-label={t('inbox.documentType')}
                className="grid grid-cols-2 gap-2 lg:grid-cols-4"
              >
                {KINDS.map(({ value, icon: Icon }) => (
                  <label
                    key={value}
                    className={cn(
                      'flex cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-sm transition-colors has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50',
                      kind === value
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'bg-background hover:bg-muted/60',
                    )}
                  >
                    <input
                      type="radio"
                      name="inbox-kind"
                      value={value}
                      className="sr-only"
                      checked={kind === value}
                      onChange={() => setKind(value)}
                    />
                    <Icon className="size-4" aria-hidden />
                    <span className="font-medium">{t(`inboxKind.${value}`)}</span>
                    <span className="text-xs text-muted-foreground">{t(`kindHint.${value}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="block space-y-1.5 text-sm">
              <span className="font-medium">
                {t('inbox.noteForAccountant')}{' '}
                <span className="font-normal text-muted-foreground">{t('decision.optional')}</span>
              </span>
              <Textarea
                aria-label={t('inbox.note')}
                maxLength={1000}
                className="min-h-16"
                placeholder={t('inbox.notePlaceholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>
          <FileDrop
            disabled={upload.isPending}
            label={upload.isPending ? t('inbox.uploading') : t('inbox.dropHere')}
            onFiles={(f) => upload.mutate(f)}
            onProblem={(m) => toast(m, { tone: 'error' })}
          />
        </section>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          className="flex flex-wrap gap-1 rounded-xl border bg-muted/40 p-1"
          role="tablist"
          aria-label={t('inbox.status')}
        >
          {CHIPS.map(({ key, icon: Icon }) => {
            const active = status === key;
            const n = counts?.[key];
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(
                  'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  active
                    ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
                )}
                onClick={() => void navigate({ to: '/documents/inbox', search: { status: key } })}
              >
                <Icon className={cn('size-4', active && CHIP_TONE[key])} aria-hidden />
                {t(`inbox.chip_${key}`)}
                {n !== undefined ? (
                  <span
                    data-testid={`inbox-count-${key}`}
                    className={cn(
                      'tnum min-w-5 rounded-full px-1.5 py-px text-center text-xs',
                      active
                        ? key === 'open' && n > 0
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {n}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <label className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute top-1/2 start-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label={t('inbox.searchLabel')}
            placeholder={t('inbox.searchPlaceholder')}
            className="bg-card ps-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      {list.isPending ? (
        <div className="divide-y rounded-xl border bg-card" aria-busy="true">
          {[0, 1, 2].map((n) => (
            <div key={n} className="flex items-center gap-4 p-4">
              <Skeleton className="size-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          ))}
        </div>
      ) : list.isError ? (
        <EmptyState title={t('inbox.loadError')} hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={
            needle
              ? t('inbox.noMatch')
              : status === 'open'
                ? t('inbox.nothingToReview')
                : t('inbox.noDocuments')
          }
          hint={
            needle
              ? t('inbox.tryDifferent')
              : canUpload
                ? t('inbox.uploadedAppear')
                : t('inbox.ownersUpload')
          }
        />
      ) : (
        <div className="space-y-5">
          {groups.map(([label, items]) => (
            <section key={label} aria-label={t(`inbox.${label}`)}>
              <h2 className="mb-2 px-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {t(`inbox.${label}`)}
              </h2>
              <ul className="divide-y overflow-hidden rounded-xl border bg-card shadow-xs">
                {items.map((i) => (
                  <InboxRow
                    key={i.id}
                    item={i}
                    staff={staff}
                    actions={
                      staff && i.status !== 'recorded' ? (
                        <RowActions
                          item={i}
                          onCreate={(k) =>
                            void navigate({
                              to: k === 'bill' ? '/expenses/bills/new' : '/expenses/expenses/new',
                              search: { fromInbox: i.id },
                            })
                          }
                          onStatus={(s) => setStatus.mutate({ item: i, status: s })}
                          onReject={() => setRejecting(i)}
                          onAi={() =>
                            i.aiDocument
                              ? void navigate({
                                  to: '/ai-accountant/$id',
                                  params: { id: i.aiDocument.id },
                                })
                              : sendToAi(i)
                          }
                        />
                      ) : null
                    }
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      <DecisionDialog
        open={Boolean(rejecting)}
        title={t('inbox.rejectTitle', {
          name: rejecting?.file.fileName ?? t('inbox.thisUpload'),
        })}
        description={t('inbox.rejectDescription')}
        confirm={t('inbox.reject')}
        destructive
        required
        label={t('inbox.reason')}
        maxLength={500}
        pending={setStatus.isPending}
        error={rejectError}
        onConfirm={(reason) =>
          rejecting && setStatus.mutate({ item: rejecting, status: 'rejected', reason })
        }
        onClose={() => {
          setRejecting(null);
          setRejectError(null);
        }}
      />
    </>
  );
}

const STATUS_STYLE: Record<InboxItem['status'], { className: string; icon: typeof InboxIcon }> = {
  received: { className: 'border-info/30 bg-info/10 text-info', icon: InboxIcon },
  in_review: { className: 'border-warning/30 bg-warning/10 text-warning', icon: Clock },
  recorded: { className: 'border-success/30 bg-success/10 text-success', icon: CircleCheck },
  rejected: {
    className: 'border-destructive/30 bg-destructive/10 text-destructive',
    icon: CircleX,
  },
};

function InboxChip({ status }: { status: InboxItem['status'] }) {
  const { className, icon: Icon } = STATUS_STYLE[status];
  const t = useT();
  return (
    <span
      data-testid="inbox-status"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {t(`inboxStatus.${status}`)}
    </span>
  );
}

const KIND_TILE: Record<InboxItem['kind'], string> = {
  bill: 'bg-primary/10 text-primary',
  receipt: 'bg-success/10 text-success',
  invoice: 'bg-info/10 text-info',
  other: 'bg-muted text-muted-foreground',
};

/** What an upload was recorded as, for the link under it. */
const DOCUMENT_LABEL = {
  bill: 'inbox.docBill',
  expense: 'inbox.docExpense',
  invoice: 'inbox.docInvoice',
} as const;

/** One upload: file, who and when, the note, what it became, and the staff actions. */
function InboxRow({
  item: i,
  staff,
  actions,
}: {
  item: InboxItem;
  staff: boolean;
  actions: ReactNode;
}) {
  const TileIcon = i.file.contentType === 'application/pdf' ? FileText : FileImage;
  const t = useT();
  return (
    <li
      data-testid="inbox-row"
      className="grid gap-3 p-4 transition-colors hover:bg-muted/30 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-start"
    >
      <div
        className={cn(
          'hidden size-10 items-center justify-center rounded-lg sm:flex',
          KIND_TILE[i.kind],
        )}
        aria-hidden
      >
        <TileIcon className="size-5" />
      </div>
      <div className="min-w-0 space-y-1.5">
        {/* the tile already shows the file type on wider screens */}
        <div className="flex flex-wrap items-center gap-2 sm:[&>span>svg]:hidden">
          <FileLink file={i.file} />
        </div>
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{t(`inboxKind.${i.kind}`)}</span>
          <span aria-hidden>·</span>
          <span className="tnum">{formatDateTime(i.uploadedAt)}</span>
          {i.uploadedBy ? (
            <>
              <span aria-hidden>·</span>
              <span>{t('inbox.by', { name: i.uploadedBy })}</span>
            </>
          ) : null}
        </p>
        {i.note ? (
          <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <MessageSquareText className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span className="[overflow-wrap:anywhere]">{i.note}</span>
          </p>
        ) : null}
        {i.status === 'rejected' && i.rejectReason ? (
          <p className="flex items-start gap-1.5 text-sm text-destructive">
            <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>{t('inbox.notRecorded', { reason: i.rejectReason })}</span>
          </p>
        ) : null}
        {i.document ? (
          <Link
            className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-sm text-primary hover:border-primary/40 hover:bg-primary/5"
            to={documentPath(i.document.kind, i.document.id)}
          >
            <ReceiptText className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">
              {t(
                DOCUMENT_LABEL[i.document.kind as keyof typeof DOCUMENT_LABEL] ?? 'inbox.docBill',
                {
                  number: i.document.number ?? t('inbox.draft'),
                },
              )}{' '}
              · {i.document.contactName}
            </span>
            <span className="tnum shrink-0 font-medium">
              {homeCurrency()} {formatAmount(i.document.total)}
            </span>
            <ArrowUpRight className="size-3.5 shrink-0" aria-hidden />
          </Link>
        ) : staff && i.aiDocument ? (
          <Link
            className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-sm text-primary hover:border-primary/40 hover:bg-primary/5"
            to="/ai-accountant/$id"
            params={{ id: i.aiDocument.id }}
          >
            <Sparkles className="size-3.5" aria-hidden />
            {t('inbox.inAi')}
            <ArrowUpRight className="size-3.5" aria-hidden />
          </Link>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end sm:self-center">
        <InboxChip status={i.status} />
        {actions}
      </div>
    </li>
  );
}

function RowActions({
  item,
  onCreate,
  onStatus,
  onReject,
  onAi,
}: {
  item: InboxItem;
  onCreate: (kind: 'bill' | 'expense') => void;
  onStatus: (s: 'received' | 'in_review') => void;
  onReject: () => void;
  /** Process with AI Accountant (staff), or open it there. */
  onAi: () => void;
}) {
  const t = useT();
  const ai = item.aiDocument ? t('inbox.openInAi') : t('inbox.processWithAi');
  // a sales invoice issued elsewhere is recorded through the AI Accountant only
  const sale = item.kind === 'invoice';
  const primary: 'bill' | 'expense' = item.kind === 'receipt' ? 'expense' : 'bill';
  return (
    <div className="inline-flex">
      <Button size="sm" className="rounded-e-none" onClick={sale ? onAi : () => onCreate(primary)}>
        {sale ? ai : primary === 'bill' ? t('inbox.createBill') : t('inbox.createExpense')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="rounded-s-none border-s border-primary-foreground/30 px-2"
            aria-label={t('inbox.moreActions')}
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {sale ? null : (
            <>
              <DropdownMenuItem onSelect={() => onCreate(primary === 'bill' ? 'expense' : 'bill')}>
                {primary === 'bill' ? t('inbox.createExpense') : t('inbox.createBill')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onAi}>{ai}</DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {item.status !== 'in_review' ? (
            <DropdownMenuItem onSelect={() => onStatus('in_review')}>
              {t('inbox.markInReview')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => onStatus('received')}>
              {t('inbox.backToReceived')}
            </DropdownMenuItem>
          )}
          {item.status !== 'rejected' ? (
            <DropdownMenuItem onSelect={onReject}>{t('inbox.cannotRecord')}</DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
